import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard, RequireRole } from '@/features/auth';
import { MainLayout } from '@/components/layout/main-layout';
import { PageLoading } from '@/components/ui/loading';
import { ADMIN_ROLES, ROLES } from '@/constants';
import { ROUTES, NotFoundPage, ROUTE_PATHS, type RouteDefinition } from '@/config/routes.config';

const LoadingFallback = () => (
  <PageLoading text="Loading page" subtitle="Please wait..." />
);

const RouteElement = ({ component: Component, access }: RouteDefinition) => {
  let element = (
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  );

  if (access === 'superadmin') {
    element = <RequireRole allowed={[ROLES.SUPER_ADMIN]}>{element}</RequireRole>;
  } else if (access === 'admin') {
    element = <RequireRole allowed={ADMIN_ROLES}>{element}</RequireRole>;
  }

  return element;
};

const createRoute = (config: RouteDefinition) => (
  <Route key={config.path} path={config.path} element={<RouteElement {...config} />} />
);

export function AppRouter() {
  const publicRoutes = ROUTES.filter(route => route.access === 'public');
  const protectedRoutes = ROUTES.filter(route => route.access !== 'public');

  return (
    <Routes>
      {publicRoutes.map(createRoute)}

      <Route
        path="/"
        element={
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to={ROUTE_PATHS.OVERVIEW} replace />} />
        {protectedRoutes.map(createRoute)}
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
